import React from 'react';
import { Link } from 'react-router-dom';
import Hero from '../components/Hero';
import SectionHeader from '../components/SectionHeader';
import GoalCard from '../components/GoalCard';
import '../styles/AboutPage.css';
import SEO from '../components/SEO';
import { WHO_WE_ARE, VISION, MISSION, GOALS, APPROACH, WHY_EF21 } from '../data/content';

const AboutPage: React.FC = () => {
  return (
    <>
      <SEO
        title="About Us"
        description="Empower Future 21 (EF21) is an independent, women-led Myanmar organization advancing equitable access to education and strengthening community resilience in crisis-affected settings."
        keywords="about EF21, Empower Future 21, women-led organization, Myanmar, humanitarian, vision, mission, goals, locally led, community resilience"
      />
      <Hero
        title="About Empower Future 21"
        subtitle="An independent, women-led Myanmar organization advancing equitable access to education and strengthening community resilience in crisis-affected settings"
        smallHero={false}
      />

      {/* Who We Are */}
      <section className="who-we-are-section">
        <div className="container">
          <SectionHeader title="Who We Are" />

          <div className="who-we-are-text">
            {WHO_WE_ARE.map((paragraph, index) => (
              <p key={index}>{paragraph}</p>
            ))}
          </div>
        </div>
      </section>

      {/* Mission & Vision Cards */}
      <section className="mission-vision-section">
        <div className="container">
          <div className="mission-vision-grid">
            <div className="vision-card">
              <div className="card-icon">✨</div>
              <h3>Our Vision</h3>
              <p>{VISION}</p>
            </div>
            <div className="mission-card">
              <div className="card-icon">🎯</div>
              <h3>Our Mission</h3>
              <p>{MISSION}</p>
            </div>
          </div>
        </div>
      </section>

      <section className="target-audience">
        <div className="container">
          <SectionHeader
            title="Who We Serve"
            subtitle="Our programmes reach the communities most affected by conflict, displacement, and crisis"
          />
          <div className="audience-grid">
            <div className="audience-card">
              <span className="audience-icon">🧒</span>
              <h4>Children</h4>
            </div>
            <div className="audience-card">
              <span className="audience-icon">🎯</span>
              <h4>Adolescents and Youth</h4>
            </div>
            <div className="audience-card">
              <span className="audience-icon">👩</span>
              <h4>Women and Girls</h4>
            </div>
            <div className="audience-card">
              <span className="audience-icon">👨‍🏫</span>
              <h4>Volunteer Teachers</h4>
            </div>
            <div className="audience-card">
              <span className="audience-icon">📚</span>
              <h4>Community Educators</h4>
            </div>
            <div className="audience-card">
              <span className="audience-icon">🤝</span>
              <h4>Community Leaders</h4>
            </div>
            <div className="audience-card">
              <span className="audience-icon">🏘️</span>
              <h4>Conflict-Affected Communities</h4>
            </div>
            <div className="audience-card">
              <span className="audience-icon">⛺</span>
              <h4>Displaced Populations (IDPs)</h4>
            </div>
            <div className="audience-card">
              <span className="audience-icon">♿</span>
              <h4>Children with Disabilities</h4>
            </div>
            <div className="audience-card">
              <span className="audience-icon">🌍</span>
              <h4>Ethnic Minorities</h4>
            </div>
            <div className="audience-card">
              <span className="audience-icon">🏛️</span>
              <h4>Local Organizations</h4>
            </div>
          </div>
        </div>
      </section>

      {/* How We Work */}
      <section id="approach" className="approach-section">
        <div className="container">
          <SectionHeader
            title="How We Work"
            subtitle="Our approach to locally led, evidence-informed humanitarian and development programming"
          />

          <div className="about-grid">
            {APPROACH.map((item, index) => (
              <div className="about-card" key={index}>
                <div className="about-icon" aria-hidden="true">{item.icon}</div>
                <h4>{item.title}</h4>
                <p>{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why EF21 */}
      <section id="why-ef21" className="why-section">
        <div className="container">
          <SectionHeader
            title="Why EF21?"
            subtitle="What we bring to partnerships and to the communities we serve"
          />

          <div className="audience-grid">
            {WHY_EF21.map((reason, index) => (
              <div className="audience-card" key={index}>
                <span className="audience-icon" aria-hidden="true">{reason.icon}</span>
                <h4>{reason.title}</h4>
                <p className="reason-description">{reason.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="goals" className="goals-section">
        <div className="container">
          <SectionHeader
            title="Our Goals"
            subtitle="Five goals guide our programmes and our growth as a national organization"
          />

          {GOALS.map(goal => (
            <GoalCard
              key={goal.number}
              number={goal.number}
              title={goal.title}
              description={goal.description}
            />
          ))}

          <div className="about-cta">
            <Link to="/what-we-do" className="btn">Explore Our Work</Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default AboutPage;
